import axios from 'axios'
import { Message } from 'element-ui'

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || 'http://127.0.0.1:8000', // api的base_url
  timeout: 10000 // 请求超时时间
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    // 对请求错误做些什么
    console.log(error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 对响应数据做点什么
    const res = response.data
    
    // 如果返回的状态码不是200，说明接口出错了
    if (res.code !== 200) {
      Message({
        message: res.msg || '系统错误',
        type: 'error',
        duration: 5 * 1000
      })
      
      // 401: 未登录或token过期
      // 403: 权限不足
      if (res.code === 401 || res.code === 403) {
        // 重新登录
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        window.location.href = '/login'
      }
      
      return Promise.reject(res)
    } else {
      return res
    }
  },
  error => {
    console.log('err' + error)
    let message = error.message
    
    if (error.response) {
      switch (error.response.status) {
        case 400:
          message = '请求参数错误'
          break
        case 401:
          message = '未授权，请登录'
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          window.location.href = '/login'
          break
        case 403:
          message = '拒绝访问'
          break
        case 404:
          message = '请求地址不存在'
          break
        case 500:
          message = '服务器内部错误'
          break
        default:
          message = `连接出错(${error.response.status})!`
      }
    } else if (error.code === 'ECONNABORTED') {
      message = '请求超时'
    } else {
      message = '网络连接异常'
    }
    
    Message({
      message: message,
      type: 'error',
      duration: 5 * 1000
    })
    
    return Promise.reject(error)
  }
)

export default service

/**
 * 把 blob 读成文本
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
function readBlobText(blob) {
  return new Promise((resolve, reject) => {
    if (typeof blob.text === 'function') {
      blob.text().then(resolve, reject)
      return
    }
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsText(blob, 'utf-8')
  })
}

/**
 * 下载导出文件（CSV 等）
 *
 * 之前后台是用 window.open 直接开导出链接下载的，那样带不了
 * Authorization 头，后端只能把导出接口放进免登录白名单——
 * 结果就是任何人都能导出全部订单（收货人、电话、完整地址）。
 *
 * 这里改用带 token 的请求拿 blob 再触发下载：
 *   - 复用 service 的 baseURL，开发/生产的路径前缀自动一致
 *   - 手动带上 Authorization 头，后端可以正常鉴权
 *   - 不走 service 的响应拦截器（blob 里没有 code 字段，
 *     走拦截器会被判成「系统错误」）
 *
 * 后端出错时返回的也是 blob（内容是 JSON），这里额外判一下，
 * 否则会出现「下载下来一个叫 xxx.csv 的 JSON 文件」这种坑。
 *
 * @param {string} url     接口路径，相对 baseURL
 * @param {object} params  查询参数
 * @param {string} filename  下载文件名
 * @returns {Promise}
 */
export function downloadFile(url, params = {}, filename = 'export.csv') {
  const token = localStorage.getItem('token')

  return axios
    .get(url, {
      baseURL: process.env.VUE_APP_BASE_API || 'http://127.0.0.1:8000',
      params,
      responseType: 'blob',
      timeout: 60000,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
    .then(async response => {
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })

      // 后端返回的是 JSON 错误体时，别当成 CSV 存下来
      try {
        const text = (await readBlobText(blob)).trim()
        if (text.charAt(0) === '{') {
          const parsed = JSON.parse(text)
          if (parsed && typeof parsed.code !== 'undefined' && parsed.code !== 200) {
            const err = new Error(parsed.msg || '导出失败')
            err.bizCode = parsed.code
            throw err
          }
        }
      } catch (e) {
        if (e && e.bizCode) throw e
        // 不是 JSON，说明确实是文件内容，继续下载
      }

      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    })
    .catch(error => {
      let message = '导出失败，请稍后重试'
      const bizCode = error.bizCode

      if (bizCode === 401 || (error.response && error.response.status === 401)) {
        message = '未授权，请重新登录'
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        window.location.href = '/login'
      } else if (bizCode === 403 || (error.response && error.response.status === 403)) {
        message = '拒绝访问'
      } else if (error && error.message) {
        message = error.message
      }
      Message({ message, type: 'error', duration: 5 * 1000 })
      return Promise.reject(error)
    })
}